import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Loader2 } from 'lucide-react';
import { clientsAPI } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { usePrivacy } from '../../contexts/PrivacyContext';

const EditPlanAmountDialog = ({ client, onUpdate }) => {
  const { formatMoney } = usePrivacy();
  const formatCurrency = formatMoney; // Alias for compatibility
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [planAmount, setPlanAmount] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [wefDate, setWefDate] = useState('');
  const [reason, setReason] = useState('');
  const [terminatePlan, setTerminatePlan] = useState(false);

  useEffect(() => {
    if (open && client) {
      setPlanAmount(client.plan_amount || client.mrr || 0);
      setTotalUsers(client.total_users || 0);
      // Default WEF to today for flexibility
      setWefDate(new Date().toISOString().split('T')[0]);
      setReason('');
      setTerminatePlan(false);
    }
  }, [open, client]);

  const handleSubmit = async () => {
    if ((!terminatePlan && !planAmount) || !wefDate || !reason) {
      toast({
        variant: 'destructive',
        title: 'Missing details',
        description: 'Please fill all required fields.'
      });
      return;
    }

    try {
      setLoading(true);
      await clientsAPI.updatePlanAmount(client.id, {
        plan_amount: terminatePlan ? 0 : parseFloat(planAmount),
        total_users: parseInt(totalUsers),
        wef: new Date(wefDate).toISOString(),
        reason: reason,
        terminate: terminatePlan
      });
      
      if (onUpdate) onUpdate(planAmount);
      setOpen(false);
    } catch (error) {
      console.error('Failed to update plan:', error);
      toast({
        variant: 'destructive',
        title: 'Failed to update plan',
        description: error.response?.data?.detail || error.message
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 ml-2" onClick={() => setOpen(true)}>
        <span className="sr-only">Edit</span>
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-pencil"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Revise Plan Pricing</DialogTitle>
            <DialogDescription>
              Update the monthly plan amount. This will affect future invoices and projections from the WEF date.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="terminate" className="text-right">
                Terminate
              </Label>
              <div className="col-span-3 flex items-center gap-2">
                <input
                  id="terminate"
                  type="checkbox"
                  checked={terminatePlan}
                  onChange={(e) => setTerminatePlan(e.target.checked)}
                />
                <span className="text-sm text-slate-600">Cancel all upcoming receivables & incentives</span>
              </div>
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="plan_amount" className="text-right">
                Monthly Amount
              </Label>
              <Input
                id="plan_amount"
                type="number"
                value={planAmount}
                onChange={(e) => setPlanAmount(e.target.value)}
                className="col-span-3"
                disabled={terminatePlan}
              />
            </div>
            
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="total_users" className="text-right">
                Total Users
              </Label>
              <Input
                id="total_users"
                type="number"
                value={totalUsers}
                onChange={(e) => setTotalUsers(e.target.value)}
                className="col-span-3"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="wef" className="text-right">
                WEF Date
              </Label>
              <Input
                id="wef"
                type="date"
                value={wefDate}
                onChange={(e) => setWefDate(e.target.value)}
                className="col-span-3"
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="reason" className="text-right">
                Reason
              </Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is the price changing?"
                className="col-span-3"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default EditPlanAmountDialog;
